/**
 * The Auto-generate tile of the plan's facts strip (renamed from "Auto",
 * 19/10/2026): its switch (Phase 8 opt-in, manage permission) with On /
 * Off, the schedule ("Daily at 23:00 🇩🇰" / "Not scheduled") and the status
 * — when it runs next (the scheduler's own time), or that you press
 * Generate now.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Bot } from 'lucide-react';

import { Switch } from '@/components/ui';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';
import { getApiErrorMessage } from '@/lib/utils';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { useSchedulerStatus } from '../../../useDropGenerator';
import { useDropGeneratorMutations } from '../../../useDropGeneratorMutations';
import { anyGroupAutoSends } from '../../../utils/plan-status';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { nextGeneration } from '../facts/generation-fact';
import { DkTime } from './DkTime';
import { FactTile } from './FactTile';

const hint = (autoSend: boolean) =>
  `Auto-generate on: the drops are generated every day at the drop generation time (drafts only — ${autoSend ? 'campaign groups with Auto-send on send theirs, you send the rest' : 'you still send'}) and verdicts are judged every 15 minutes while the day runs. Off: someone presses Generate now and Evaluate verdicts.`;

function useAutoGenerateToggle(countryKey: string) {
  const { updatePlan } = useDropGeneratorMutations();
  const { success, error } = useToast();
  const toggle = async (autoGenerate: boolean) => {
    try {
      await updatePlan.mutateAsync({ countryKey, autoGenerate });
      success(autoGenerate ? 'Auto-generate on — the drops generate themselves' : 'Auto-generate off — someone presses Generate now');
    } catch (err) {
      error('Could not change Auto-generate', getApiErrorMessage(err));
    }
  };
  return { toggle, busy: updatePlan.isPending };
}

/** "Daily at 23:00 −1d 🇩🇰" on, "Not scheduled" off. */
const Schedule: FC<{ plan: DropGenPlanDetail }> = ({ plan }) => {
  const { runDate } = useCountryClock();
  if (!plan.autoGenerate) return <>Not scheduled</>;
  return (
    <span data-testid="schedule-fact">
      Daily at <DkTime min={plan.generateAtMin} tz={plan.recipientTimezone} runDate={runDate} />
      {plan.recipientTimezone ? ' 🇩🇰' : ''}
    </span>
  );
};

export const AutoSegment: FC<{ plan: DropGenPlanDetail; now?: Date }> = ({ plan, now }) => {
  const { hasPermission } = useAuth();
  const { toggle, busy } = useAutoGenerateToggle(plan.countryKey);
  const mine = useSchedulerStatus().data?.plans.find((p) => p.countryKey === plan.countryKey);
  const off = { text: 'You press Generate now', tone: 'muted' as const };
  const status = plan.autoGenerate ? (nextGeneration(mine, now) ?? off) : off;
  return (
    <FactTile icon={Bot} label="Auto-generate" hint={hint(anyGroupAutoSends(plan))} detail={<Schedule plan={plan} />} status={status} testId="fact-auto">
      <label className="inline-flex items-center gap-2.5">
        <Switch
          checked={plan.autoGenerate}
          disabled={!hasPermission('drop_generator.manage') || busy}
          onCheckedChange={(on) => void toggle(on)}
          aria-label="Auto-generate — the drops generate themselves every day"
        />
        {plan.autoGenerate ? 'On' : 'Off'}
      </label>
    </FactTile>
  );
};
